// Demo mode — admin-only anonymization used to preview the dashboard with
// prospective customers without revealing identifiable partner information.
//
// When demo mode is on we deterministically map each grid (by id) to a stable
// fake lake name, fake town, and a fake location scattered around a single demo
// center. "Deterministic" matters: the same grid must render the same fake
// identity on the list page, the deployment map, and the detail page, and stay
// stable across navigation within a session.

// The single geolocation the "lake view" on a grid's Current Status tab pulls
// from while in demo mode (Minneapolis, MN).
export const DEMO_CENTER = { lat: 44.9220156, lng: -93.3053488 };

const FAKE_LAKE_NAMES = [
  'Cedar Lake', 'Emerald Lake', 'Silver Lake', 'Blue Heron Lake', 'Loon Lake',
  'Birchwood Lake', 'Clearwater Lake', 'Maple Lake', 'Pinecrest Lake', 'Willow Lake',
  'Otter Lake', 'Sunrise Lake', 'Crystal Lake', 'Whitetail Lake', 'Turtle Lake',
  'Aspen Lake', 'Meadow Lake', 'Mallard Lake', 'Spruce Lake', 'Hidden Lake',
  'Eagle Lake', 'Fox Lake', 'Granite Lake', 'Harmony Lake', 'Ironwood Lake',
  'Juniper Lake', 'Kingfisher Lake', 'Lakeview Pond', 'Moonlight Lake', 'Northstar Lake',
  'Oakridge Lake', 'Pelican Lake', 'Quiet Lake', 'Redwing Lake', 'Sandpiper Lake',
  'Timberline Lake', 'Vista Lake', 'Wildrose Lake', 'Yellowbirch Lake', 'Zephyr Lake',
];

const FAKE_TOWNS = [
  { city: 'Elmwood', region: 'MN' }, { city: 'Fairhaven', region: 'MN' },
  { city: 'Greenfield', region: 'MN' }, { city: 'Lakeside', region: 'WI' },
  { city: 'Riverton', region: 'MN' }, { city: 'Springvale', region: 'MN' },
  { city: 'Brookside', region: 'WI' }, { city: 'Cedar Falls', region: 'MN' },
  { city: 'Northbrook', region: 'MN' }, { city: 'Oakdale', region: 'WI' },
  { city: 'Pinehurst', region: 'MN' }, { city: 'Westport', region: 'MN' },
  { city: 'Maplewood', region: 'WI' }, { city: 'Silverton', region: 'MN' },
  { city: 'Havenwood', region: 'MN' }, { city: 'Clearview', region: 'WI' },
];

// FNV-1a style hash so a grid id (number or string) maps stably to a bucket.
const hashId = (id, salt = 0) => {
  let h = (2166136261 ^ salt) >>> 0;
  const s = String(id ?? '');
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};

export const demoGridName = (id) => FAKE_LAKE_NAMES[hashId(id, 1) % FAKE_LAKE_NAMES.length];

export const demoTown = (id) => FAKE_TOWNS[hashId(id, 2) % FAKE_TOWNS.length];

// A stable fake location for a grid, scattered in a ring around DEMO_CENTER so
// multiple grids don't stack on top of each other on the map. ~2–8 km out.
export const demoGridLatLng = (id) => {
  const h = hashId(id, 3);
  const angle = (h % 360) * (Math.PI / 180);
  const radiusDeg = 0.02 + ((h >> 9) % 5) * 0.015; // ≈ 2.2–8.8 km
  const latScale = Math.max(0.2, Math.cos(DEMO_CENTER.lat * Math.PI / 180));
  return {
    lat: DEMO_CENTER.lat + radiusDeg * Math.cos(angle),
    lng: DEMO_CENTER.lng + (radiusDeg * Math.sin(angle)) / latScale,
  };
};

// A grid's fake buoy position: the grid's demo point plus a small per-buoy jitter
// so multiple buoys in one grid remain distinguishable on the map.
export const demoBuoyLatLng = (gridId, mooringId) => {
  const base = demoGridLatLng(gridId);
  const h = hashId(`${gridId}:${mooringId}`, 4);
  const jitter = 0.0012;
  return {
    lat: base.lat + (((h % 200) / 100) - 1) * jitter,
    lng: base.lng + ((((h >> 8) % 200) / 100) - 1) * jitter,
  };
};

// Returns a copy of a grid with all identifiable fields replaced by their stable
// demo equivalents. Keeps `id` (and every other field) intact so data lookups by
// id still work — only the human-identifiable fields are swapped.
export const anonymizeGrid = (grid) => {
  if (!grid) return grid;
  const town = demoTown(grid.id);
  const loc = demoGridLatLng(grid.id);
  return {
    ...grid,
    name: demoGridName(grid.id),
    qualifier: null,
    city: town.city,
    region: town.region,
    country: 'USA',
    lat: loc.lat,
    lon: loc.lng,
  };
};
